/**
 * @fileoverview Application Root Component
 *
 * Purpose: Sets up provider hierarchy for the application.
 *
 * Providers (outer to inner):
 * - HelmetProvider: HTML head management
 * - PostHogProvider: Analytics
 * - AuthKitProvider: WorkOS authentication (hosted UI)
 * - ConvexProviderWithAuthKit: Convex client authenticated via WorkOS JWT
 * - QueryClientProvider: TanStack Query for Convex data
 * - RouterProvider: TanStack Router
 */

import { ConvexReactClient } from "convex/react";
import { RouterProvider } from "@tanstack/react-router";
import { ConvexQueryClient } from "@convex-dev/react-query";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { AuthKitProvider, useAuth } from "@workos-inc/authkit-react";
import { ConvexProviderWithAuthKit } from "@convex-dev/workos";
import { router } from "@/router";
import { PostHogProvider } from "@/utils/posthog";
import { Toaster as SonnerToaster } from "sonner";
import "@/i18n";

// Convex client
const convex = new ConvexReactClient(import.meta.env.VITE_CONVEX_URL as string);

const convexQueryClient = new ConvexQueryClient(convex);
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryKeyHashFn: convexQueryClient.hashFn(),
      queryFn: convexQueryClient.queryFn(),
    },
  },
});

convexQueryClient.connect(queryClient);

function InnerApp() {
  return <RouterProvider router={router} context={{ queryClient }} />;
}

const helmetContext = {};

export default function App() {
  return (
    <HelmetProvider context={helmetContext}>
      <PostHogProvider>
        <AuthKitProvider
          clientId={import.meta.env.VITE_WORKOS_CLIENT_ID}
          redirectUri={import.meta.env.VITE_WORKOS_REDIRECT_URI}
          devMode
          onRefreshFailure={() => {
            window.location.href = "/login";
          }}
        >
          <ConvexProviderWithAuthKit client={convex} useAuth={useAuth}>
            <QueryClientProvider client={queryClient}>
              <InnerApp />
              <SonnerToaster richColors position="top-right" />
            </QueryClientProvider>
          </ConvexProviderWithAuthKit>
        </AuthKitProvider>
      </PostHogProvider>
    </HelmetProvider>
  );
}
